// BL-177 Step 2j: the "Current activity" half of a Hybrid client card.
//
// Pure functions: fetched rows in, a small model per account out. Nothing here talks to the network (the hook in
// src/components/clients/useCurrentActivity.js does, through the database function crm_current_activity).
//
// PRIVACY. The database already hides conversations the viewer cannot see (migration 212's conv_row_visible, applied by the
// crm_current_activity function under the caller's own row-level security). This file is the second lock: any row that is flagged as
// coming from a conversation the viewer cannot see (conversation_hidden = true, or visible = false / conversation_visible = false) is
// dropped before anything is built from it, so such a row contributes no message time, no open item and no route.
//
// The model for one account:
//   trip        the thing to show first: the NEXT trip, else the latest QUOTE, else the LAST trip - or null  ("No trips yet")
//   quote       the latest quote when it is not already the trip shown, else null
//   lastMessage { channel, channelLabel, direction: 'inbound'|'outbound', at, atLabel } or null
//   openItems   plain-English strings ("Awaiting confirmation", "Flight number not confirmed"), [] = "No open items"

export const NO_TRIPS_TEXT = 'No trips yet';
export const NO_OPEN_ITEMS_TEXT = 'No open items';
export const NO_MESSAGES_TEXT = 'No messages yet';
export const AWAITING_CONFIRMATION = 'Awaiting confirmation';

export const EMPTY_ACTIVITY = Object.freeze({ trip: null, quote: null, lastMessage: null, openItems: Object.freeze([]), openItemsHidden: 0 });

const ACTIVE_STATUSES = new Set(['confirmed', 'scheduled', 'in_progress']);
const FINISHED_STATUSES = new Set(['completed', 'closed']);
const GRACE_MS = 6 * 60 * 60 * 1000;   // a trip that started up to 6 hours ago still counts as "next" (it may be under way)
const MAX_OPEN_ITEMS = 4;

const CHANNEL_LABELS = { sms: 'Text', mms: 'Text', whatsapp: 'WhatsApp', email: 'Email', voice: 'Call', call: 'Call', web: 'Web chat', chat: 'Chat' };

const isHidden = (row) => !row || row.conversation_hidden === true || row.visible === false || row.conversation_visible === false;
const ms = (value) => {
    const time = value ? new Date(value).getTime() : NaN;
    return Number.isNaN(time) ? null : time;
};

/** "Tue, Oct 14" and "8:00 AM" for an instant, in the viewer's zone (or `timeZone` in tests). */
export function formatWhen(value, { timeZone } = {}) {
    const time = ms(value);
    if (time === null) return { date: '', time: '', both: '' };
    const at = new Date(time);
    const date = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone }).format(at);
    const clock = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone }).format(at).replace(/[  ]/g, ' ');
    return { date, time: clock, both: `${date}, ${clock}` };
}

/** First part of an address ("12 Peachtree St"), kept short for a one-line route. */
export function shortPlace(text, limit = 28) {
    const first = String(text || '').split(',')[0].replace(/\s+/g, ' ').trim();
    if (!first) return '';
    return first.length > limit ? `${first.slice(0, limit - 1).trimEnd()}…` : first;
}

export function routeOf(trip) {
    const from = shortPlace(trip?.pickup_address || trip?.origin);
    const to = shortPlace(trip?.dropoff_address || trip?.destination);
    if (from && to) return `${from} → ${to}`;
    return from || to || '';
}

const money = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);

function tripView(row, kind, { prices, timeZone }) {
    const startsAt = row.scheduled_start || null;
    const when = formatWhen(startsAt || row.created_at, { timeZone });
    const rawAmount = prices?.[row.id]?.quoted_amount ?? prices?.get?.(row.id)?.quoted_amount;
    const amount = rawAmount === null || rawAmount === undefined || Number.isNaN(Number(rawAmount)) ? null : Number(rawAmount);
    return {
        kind,
        label: kind === 'next' ? 'Next trip' : kind === 'quote' ? 'Latest quote' : 'Last trip',
        id: row.id,
        confirmationNumber: row.confirmation_number || null,
        status: row.status,
        service: row.service_label || row.service_type || '',
        startsAt,
        dateLabel: startsAt ? when.date : '',
        timeLabel: startsAt ? when.time : '',
        route: routeOf(row),
        amount,
        amountLabel: kind === 'quote' && amount !== null ? money(amount) : '',
        // a quote with no date yet shows when it was asked for
        askedLabel: !startsAt ? when.date : '',
    };
}

/** "Flight number not confirmed" for each detail still missing. */
export function labelsFromProblems(problems) {
    const list = Array.isArray(problems) ? problems : [];
    return list.map((problem) => String(problem?.label || problem?.field || '').trim()).filter(Boolean).map((label) => `${label} not confirmed`);
}

/**
 * Turn the rows crm_current_activity returned into one model per account.
 * @param {{ trips?: object[], needsInfo?: object[], messages?: object[], reservations?: object[],
 *           prices?: Record<string, {quoted_amount?: number|string|null}>, now?: Date|string|number, timeZone?: string }} rows
 * @returns {Record<string, typeof EMPTY_ACTIVITY>}  keyed by account id; an account with nothing is simply absent (use EMPTY_ACTIVITY)
 */
export function buildCurrentActivity({ trips = [], needsInfo = [], messages = [], reservations = [], prices = {}, now = new Date(), timeZone } = {}) {
    const nowMs = ms(now) ?? Date.now();
    const accounts = new Map();
    const slot = (accountId) => {
        if (!accounts.has(accountId)) accounts.set(accountId, { trips: [], needsInfo: [], messages: [], reservations: [] });
        return accounts.get(accountId);
    };
    (trips || []).forEach((row) => { if (row?.account_id) slot(row.account_id).trips.push(row); });
    (needsInfo || []).filter((row) => !isHidden(row)).forEach((row) => { if (row?.account_id) slot(row.account_id).needsInfo.push(row); });
    (messages || []).filter((row) => !isHidden(row)).forEach((row) => { if (row?.account_id) slot(row.account_id).messages.push(row); });
    (reservations || []).filter((row) => !isHidden(row)).forEach((row) => { if (row?.account_id) slot(row.account_id).reservations.push(row); });

    const result = {};
    accounts.forEach((rows, accountId) => {
        const ordered = rows.trips.filter((trip) => trip && trip.status !== 'canceled' && trip.status !== 'no_show');
        const startOf = (trip) => ms(trip.scheduled_start);
        const upcoming = ordered.filter((trip) => ACTIVE_STATUSES.has(trip.status) && startOf(trip) !== null && startOf(trip) >= nowMs - GRACE_MS)
            .sort((a, b) => startOf(a) - startOf(b))[0];
        const quote = ordered.filter((trip) => trip.status === 'requested')
            .sort((a, b) => (ms(b.created_at) || 0) - (ms(a.created_at) || 0))[0];
        const last = ordered.filter((trip) => FINISHED_STATUSES.has(trip.status) || (ACTIVE_STATUSES.has(trip.status) && startOf(trip) !== null && startOf(trip) < nowMs - GRACE_MS))
            .sort((a, b) => (startOf(b) ?? ms(b.created_at) ?? 0) - (startOf(a) ?? ms(a.created_at) ?? 0))[0];

        const shown = upcoming ? tripView(upcoming, 'next', { prices, timeZone })
            : quote ? tripView(quote, 'quote', { prices, timeZone })
                : last ? tripView(last, 'last', { prices, timeZone }) : null;
        const alsoQuote = quote && shown?.kind !== 'quote' ? tripView(quote, 'quote', { prices, timeZone }) : null;

        const latestMessage = rows.messages
            .map((row) => ({ row, inbound: ms(row.last_inbound_at), outbound: ms(row.last_outbound_at) }))
            .map((entry) => ({ ...entry, at: Math.max(entry.inbound ?? -Infinity, entry.outbound ?? -Infinity) }))
            .filter((entry) => Number.isFinite(entry.at))
            .sort((a, b) => b.at - a.at)[0];
        const lastMessage = latestMessage ? {
            channel: latestMessage.row.channel || '',
            channelLabel: CHANNEL_LABELS[String(latestMessage.row.channel || '').toLowerCase()] || String(latestMessage.row.channel || 'Message'),
            direction: (latestMessage.inbound ?? -Infinity) >= (latestMessage.outbound ?? -Infinity) ? 'inbound' : 'outbound',
            at: new Date(latestMessage.at).toISOString(),
            atLabel: formatWhen(latestMessage.at, { timeZone }).both,
        } : null;

        const items = [];
        const add = (text) => { if (text && !items.some((item) => item.toLowerCase() === text.toLowerCase())) items.push(text); };
        if (ordered.some((trip) => trip.status === 'requested')) add(AWAITING_CONFIRMATION);
        rows.needsInfo.forEach((row) => labelsFromProblems(row.needs_info_fields).forEach(add));
        rows.reservations.forEach((row) => {
            const missing = labelsFromProblems(row.problems);
            if (missing.length) missing.forEach(add);
            else if (row.status === 'pending_review') add('Reservation awaiting review');
            else add('Reservation details missing');
        });

        if (shown || alsoQuote || lastMessage || items.length) {
            result[accountId] = {
                trip: shown, quote: alsoQuote, lastMessage,
                openItems: items.slice(0, MAX_OPEN_ITEMS), openItemsHidden: Math.max(0, items.length - MAX_OPEN_ITEMS),
            };
        }
    });
    return result;
}

/** The account ids to ask about: de-duplicated, blanks dropped, in chunks the database function accepts. */
export function chunkAccountIds(ids, size = 1000) {
    const unique = [...new Set((ids || []).filter(Boolean))];
    const chunks = [];
    for (let index = 0; index < unique.length; index += size) chunks.push(unique.slice(index, index + size));
    return chunks;
}

/** Merge the `{ trips, needs_info, messages, reservations }` objects of several crm_current_activity calls into the argument of buildCurrentActivity. */
export function mergeActivityPayloads(payloads) {
    const merged = { trips: [], needsInfo: [], messages: [], reservations: [] };
    (payloads || []).forEach((payload) => {
        if (!payload) return;
        merged.trips.push(...(payload.trips || []));
        merged.needsInfo.push(...(payload.needs_info || []));
        merged.messages.push(...(payload.messages || []));
        merged.reservations.push(...(payload.reservations || []));
    });
    return merged;
}
